import React from 'react';
import clsx from 'clsx';
import {ERROR, FALLBACK} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useDrivingInfoNa, {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.na';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

export default function FuelEfficiencySectionNa({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const {drivingData} = useDrivingInfoNa({vin});
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  return (
    <div className="section_container trip_month_efficiency">
      <LinkTo
        to={ROUTE_PATHS.HISTORY.getFullPath(vin)}
        className="section_container_full_btn"
        aria-label={t('msg_home_cumulative_title')}
        onClickBypass={() =>
          emitEventLog({
            event: 'di_driveinfo_interaction',
            data_action: 'view banner',
            data_name: t('msg_home_cumulative_title'),
          })
        }
      />
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('msg_home_cumulative_title')}</h3>
      </div>
      <div className="trip_month">
        <div
          className="trip_month_info"
          role="text"
          aria-label={`${formatNumberWithLimitAndCommas(convertDistanceValue(drivingData.odometer))}${convertDistanceSymbol(drivingData.odometer)}`}
        >
          <span className="trip_month_value">{formatNumberWithLimitAndCommas(convertDistanceValue(drivingData.odometer))}</span>
          <span className="trip_month_unit">{convertDistanceSymbol(drivingData.odometer)}</span>
        </div>
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_home_cumulative_driven')}
        </p>
        <div className="trip_month_imgbox">
          <ImageSelector
            kiaLightSrc={() => import('@di-shared/assets/img/home/driving_record_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/driving_record.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/driving_record_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/driving_record.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/driving_record_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/driving_record.png')}
          />
        </div>
      </div>
    </div>
  );
}

// FALLBACK(LOADING, ERROR) UI
FuelEfficiencySectionNa.Error = () => <FuelEfficiencySectionFallback fallback={FALLBACK.ERROR} />;
FuelEfficiencySectionNa.Loading = () => <FuelEfficiencySectionFallback fallback={FALLBACK.LOADING} />;

function FuelEfficiencySectionFallback({fallback}: {fallback: FALLBACK}) {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  return (
    <div
      className={clsx('section_container trip_month_efficiency', {skeleton: fallback === FALLBACK.LOADING})}
      aria-hidden={fallback === FALLBACK.LOADING && 'true'}
    >
      <div className="section_container_header">
        <h3 className="body_medium_b">{t('msg_home_cumulative_title')}</h3>
      </div>
      <div className="trip_month">
        <div
          className="trip_month_info"
          role="text"
          aria-label={`${ERROR.DASH}${defaultDistanceSymbol}`}
        >
          <span className="trip_month_value">{ERROR.DASH}</span>
          <span className="trip_month_unit">{defaultDistanceSymbol}</span>
        </div>
        <p
          className="trip_month_desc"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_home_cumulative_driven')}
        </p>
        <div className="trip_month_imgbox">
          <ImageSelector
            kiaLightSrc={() => import('@di-shared/assets/img/home/driving_record_light.png')}
            kiaDarkSrc={() => import('@di-shared/assets/img/home/driving_record.png')}
            hyundaiLightSrc={() => import('@di-shared/assets/img/home/hyundai/driving_record_light.png')}
            hyundaiDarkSrc={() => import('@di-shared/assets/img/home/hyundai/driving_record.png')}
            genesisLightSrc={() => import('@di-shared/assets/img/home/genesis/driving_record_light.png')}
            genesisDarkSrc={() => import('@di-shared/assets/img/home/genesis/driving_record.png')}
          />
        </div>
      </div>
    </div>
  );
}
